/**
 * NewsletterSignup — s'abonner aux actualités par email, sans compte.
 *
 * Une adresse, un clic : l'API envoie un email de confirmation (double
 * opt-in) ; rien ne part tant que le lien n'a pas été cliqué. Le champ
 * « website » est un pot de miel invisible pour les robots.
 */
import { FormEvent, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../../services/api';
import { useCustomer } from '../../contexts/CustomerContext';

interface NewsletterSignupProps {
  source: 'feed' | 'footer' | 'account';
  /** `card` = bloc mis en avant (fil) ; `footer` = ligne discrète (pied de page) */
  variant?: 'card' | 'footer';
}

type State = 'idle' | 'sending' | 'pending' | 'already' | 'error';

export default function NewsletterSignup({ source, variant = 'card' }: NewsletterSignupProps) {
  const { customer } = useCustomer();
  const [email, setEmail] = useState(customer?.email || '');
  const [website, setWebsite] = useState('');
  const [state, setState] = useState<State>('idle');
  const [error, setError] = useState('');

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;
    setState('sending');
    setError('');
    try {
      const result = await api.post('/newsletter/subscribe', { email: email.trim(), source, website });
      setState(result?.status === 'already_confirmed' ? 'already' : 'pending');
    } catch (err: any) {
      const message = err.response?.data?.message;
      if (err.response?.status === 429) {
        setError('Trop de tentatives depuis votre connexion. Réessayez dans une minute.');
      } else {
        setError(Array.isArray(message) ? message.join(' ') : message || 'Impossible de vous inscrire pour le moment.');
      }
      setState('error');
    }
  };

  const isFooter = variant === 'footer';

  if (state === 'pending' || state === 'already') {
    return (
      <div
        className={
          isFooter
            ? 'text-center text-sm text-green-700'
            : 'rounded-2xl border border-green-200 bg-green-50 px-5 py-4 text-green-800'
        }
      >
        {state === 'pending' ? (
          <>
            <strong>Presque fini !</strong> Nous venons d'envoyer un email à <strong>{email}</strong> :
            cliquez sur le lien qu'il contient pour confirmer votre abonnement.
          </>
        ) : (
          <>Cette adresse est déjà abonnée — vous ne manquerez rien.</>
        )}
      </div>
    );
  }

  return (
    <form
      onSubmit={submit}
      className={
        isFooter
          ? 'mx-auto mb-8 max-w-xl text-center'
          : 'rounded-2xl border border-[#e6e2d6] bg-[#faf7f0] px-5 py-5 shadow-sm'
      }
    >
      {isFooter ? (
        <p className="mb-3 text-sm font-medium text-gray-700">
          Recevez nos actualités par email — arrivages, événements, coulisses de la ferme
        </p>
      ) : (
        <>
          <h2 className="text-lg font-bold text-[#2d6a4f]">📬 Recevoir les actualités par email</h2>
          <p className="mt-1 text-sm text-gray-600">
            Arrivages, événements, promotions et coulisses de la ferme — directement dans votre boîte
            mail, sans créer de compte.
          </p>
        </>
      )}

      <div className="mt-3 flex flex-col gap-2 sm:flex-row">
        <label className="sr-only" htmlFor={`newsletter-email-${source}`}>
          Votre adresse email
        </label>
        <input
          id={`newsletter-email-${source}`}
          type="email"
          inputMode="email"
          autoComplete="email"
          required
          placeholder="votre@email.be"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          disabled={state === 'sending'}
          className="min-h-[44px] flex-1 rounded-lg border border-gray-300 bg-white px-4 text-base text-gray-900 focus:border-[#2d6a4f] focus:outline-none focus:ring-2 focus:ring-[#2d6a4f]/30"
        />
        {/* Pot de miel : jamais affiché, jamais rempli par un humain */}
        <input
          type="text"
          name="website"
          tabIndex={-1}
          autoComplete="off"
          value={website}
          onChange={(e) => setWebsite(e.target.value)}
          className="hidden"
          aria-hidden="true"
        />
        <button
          type="submit"
          disabled={state === 'sending' || !email.trim()}
          className="min-h-[44px] rounded-lg bg-[#2d6a4f] px-5 font-semibold text-white transition-colors hover:bg-[#245840] disabled:opacity-50"
        >
          {state === 'sending' ? 'Envoi…' : "S'abonner"}
        </button>
      </div>

      {state === 'error' && <p className="mt-2 text-sm text-red-600">{error}</p>}

      <p className={`mt-2 text-xs text-gray-600 ${isFooter ? '' : 'leading-relaxed'}`}>
        Un email de confirmation vous sera envoyé. Désinscription en un clic dans chaque email.{' '}
        <Link to="/shop/privacy" className="underline underline-offset-2 hover:text-gray-700">
          Politique de confidentialité
        </Link>
      </p>
    </form>
  );
}
